import { FormBuilder, FormGroup } from '@angular/forms';
import {
  getTransformedNumber,
  isWeekendAndHolidayDisabled,
  getAmountObject,
  getDateFromCalendarObj,
  getCalendarDateObj
} from './utils';
import * as moment from 'moment';
import { VALUE_DATE_MAX } from './shared/global-meta-data';

type validationError = { [key: string]: boolean } | null;
const formBuilderInstance = new FormBuilder();
/**
 * Returns the amount formgroup with amount and amountIn form controls and default amount validators applied
 * @param validators List of validators if any to apply to the amount formgroup
 */
export function getNewAmountFg(value = null, validators = []) {
  const amountObj = value ? getAmountObject(value) : { amount: null, amountIn: 'Cr' };

  return formBuilderInstance.group({
    amount: amountObj.amount,
    amountIn: amountObj.amountIn
  }, {
    validators: getNumberValidationsToApply(validators)
  });
}

/**
 * Returns the validators to apply for the number field.
 * @param validators - List of validators if any to apply to the amount form group.
 */
export function getNumberValidationsToApply(validators = []) {
  const defaultValidators = [Multiple5L, AmountRequired, AmountPositive];
  const validatorsToApply =
    validators && validators.length > 0 ? defaultValidators.concat(validators) : defaultValidators;

  return validatorsToApply;
}
/**
 * Validation function for CP value.
 */
export function getCpValueErrors(cpValue, cpValueIn, remainingBorrowingLimit, initialCpValue = 0) {
  let cpCap = getTransformedNumber(cpValue, cpValueIn);
  if (initialCpValue) {
    cpCap -= initialCpValue;
  }
  let validationErrors: any = true;
  if (!cpValue) {
    validationErrors = {
      required: true
    };
  } else if (cpValue <= 0) {
    validationErrors = {
      notPositive: true
    };
  } else if (cpCap && cpCap > remainingBorrowingLimit) {
    validationErrors = {
      exceedsValueRemaining: true
    };
  } else if (Math.abs(cpCap) % getTransformedNumber(5, 'Lakhs') > 0) {
    validationErrors = {
      notMultiple5L: true
    };
  }
  return {
    validationErrors
  };
}

/**
* check if min bid value is multiple of 5Lakhs or not.
*/
export function amountLessThanValidator(maxValue) {
  return (fg: FormGroup) => {
    const amountVal = getTransformedNumber(
      fg.controls.amount.value,
      fg.controls.amountIn.value);
    return (amountVal > maxValue) ? { exceedsMaxValue: true } : null;
  };
}

/**
 * Check if amount is multiple of 5 Lakhs
 */
export function Multiple5L(amountFg: FormGroup): validationError {
  const amount = getTransformedNumber(amountFg.value.amount, amountFg.value.amountIn);
  return (amount % getTransformedNumber(5, 'Lakhs') > 0) ? { 'notMultiple5L': true } : null;
}

/**
 * Check if amount is required
 */
export function AmountRequired(amountFg: FormGroup): validationError {
  return !amountFg.controls.amount.value ? { required: true } : null;
}

/**
 * check if amount is positive
 */
export function AmountPositive(amountFg: FormGroup): validationError {
  return (amountFg.controls.amount.value <= 0) ? { notPositive: true } : null;
}

/**
 * check if its a holiday
 */
export function HolidayValidator(calendarDateFg: FormGroup): validationError {
  return isWeekendAndHolidayDisabled(calendarDateFg.value) ? { holidayOrWeekend: true } : null;
}

/**
 * check if date is less than equal to VALUE_DATE_MAX days from today
 */
export function maxDaysFromTodayValidator(calendarDateFg: FormGroup): validationError {
  const calDateMoment = getDateFromCalendarObj(calendarDateFg.value).endOf('day');
  const daysFromTodayMoment = moment().add(VALUE_DATE_MAX, 'days').endOf('day');
  return calDateMoment.isAfter(daysFromTodayMoment) ? { maxDaysFromToday: true } : null;
}

/**
 * Check if today plus tenure (in days) falls on a holiday
 */
export function tenureHolidayWeekendValidator(inputFormGroup: FormGroup) {
  const valueDate = inputFormGroup.get('value_date').value;
  const tenure = inputFormGroup.get('tenure').value;
  if (valueDate && tenure) {
    const tenureDate = getDateFromCalendarObj(valueDate).add(tenure, 'days').toDate();
    if (isWeekendAndHolidayDisabled(getCalendarDateObj(tenureDate))) {
      return {
        'tenure': {
          holidayOrWeekend: true
        }
      };
    }
  }
}

  /**
   * clear validation functions and errors on the formgroup for the given keys
   */
   export function clearValidators(formGroup, keys) {
    keys.forEach((key) => {
      formGroup.get(key).clearValidators();
      formGroup.get(key).setErrors(null);
    });
  }
